import { useEffect, useState } from "react";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import logo from "../../static/media/logo.webp";

export default function NavbarMobile({
  scrolled,
  navItems,
  isActive,
  authLoading,
  isAuthenticated,
  isAdmin,
  profilePhoto,
  cartCount,
  handleLogout,
  handleGoCheckout,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    setMobileOpen(false);
    setAnchorEl(null);
  }, [location.pathname]);

  const handleAccountMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAccountClick = () => {
    handleAccountMenuClose();
    navigate(isAdmin ? "/admin/dashboard/staff" : "/mi-perfil");
  };

  const handleOpenHamburgerMenu = () => {
    setMobileOpen(true);
  };

  const handleCloseHamburgerMenu = () => {
    setMobileOpen(false);
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: "rgba(255,255,255,0.96)",
          backdropFilter: "blur(14px)",
          color: "#111111",
          borderBottom: scrolled
            ? "1px solid rgba(17,17,17,0.08)"
            : "1px solid rgba(17,17,17,0.05)",
          boxShadow: scrolled
            ? "0 16px 34px rgba(0,0,0,0.08)"
            : "0 8px 24px rgba(0,0,0,0.04)",
          transition:
            "box-shadow .28s ease, border-color .28s ease, background-color .28s ease",
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              minHeight: scrolled ? 70 : 78,
              transition: "min-height .28s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                textDecoration: "none",
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="NovaXFit"
                sx={{
                  height: scrolled ? 42 : 48,
                  width: "auto",
                  display: "block",
                  transition: "height .28s ease",
                }}
              />
            </Box>

            <Stack direction="row" spacing={0.9} sx={{ alignItems: "center" }}>
              <IconButton
                onClick={handleGoCheckout}
                aria-label="Carrito"
                title="Carrito"
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 3,
                  color: "#111111",
                  border: "1px solid rgba(17,17,17,0.08)",
                  bgcolor: "#ffffff",
                  "&:hover": {
                    color: "#e50914",
                    bgcolor: "rgba(229,9,20,0.06)",
                    borderColor: "rgba(229,9,20,0.18)",
                  },
                }}
              >
                <Badge badgeContent={cartCount} color="error" overlap="circular" max={99}>
                  <ShoppingCartOutlinedIcon />
                </Badge>
              </IconButton>

              {!authLoading && isAuthenticated && (
                <IconButton
                  onClick={handleAccountMenuOpen}
                  aria-label="Abrir menú de cuenta"
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 3,
                    border: "1px solid rgba(17,17,17,0.08)",
                    bgcolor: "#ffffff",
                    "&:hover": {
                      bgcolor: "rgba(229,9,20,0.05)",
                      borderColor: "rgba(229,9,20,0.18)",
                    },
                  }}
                >
                  <Avatar
                    src={profilePhoto}
                    sx={{
                      width: 30,
                      height: 30,
                      bgcolor: "#1f1f1f",
                    }}
                  >
                    {!profilePhoto && <PersonRoundedIcon sx={{ fontSize: 17 }} />}
                  </Avatar>
                </IconButton>
              )}

              <IconButton
                onClick={handleOpenHamburgerMenu}
                aria-label="Abrir menú hamburguesa"
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 3,
                  color: "#111111",
                  border: "1px solid rgba(17,17,17,0.08)",
                  bgcolor: "#ffffff",
                  "&:hover": {
                    color: "#e50914",
                    bgcolor: "rgba(229,9,20,0.06)",
                    borderColor: "rgba(229,9,20,0.18)",
                  },
                }}
              >
                <MenuRoundedIcon />
              </IconButton>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleCloseHamburgerMenu}
        PaperProps={{
          sx: {
            width: "88%",
            maxWidth: 370,
            bgcolor: "#ffffff",
            color: "#111111",
            px: 2,
            py: 2,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2.2,
          }}
        >
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              textDecoration: "none",
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="NovaXFit"
              sx={{
                height: 42,
                width: "auto",
                display: "block",
              }}
            />
          </Box>

          <IconButton
            onClick={handleCloseHamburgerMenu}
            aria-label="Cerrar menú hamburguesa"
            sx={{
              width: 40,
              height: 40,
              borderRadius: 3,
              color: "#111111",
              border: "1px solid rgba(17,17,17,0.08)",
              bgcolor: "#ffffff",
              "&:hover": {
                color: "#e50914",
                bgcolor: "rgba(229,9,20,0.06)",
                borderColor: "rgba(229,9,20,0.18)",
              },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            mb: 2,
            p: 1.6,
            borderRadius: 4,
            bgcolor: "#111111",
            color: "#ffffff",
          }}
        >
          <Stack spacing={1.2}>
            <Box
              component="a"
              href="https://wa.me/593985224301"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                textDecoration: "none",
                color: "#ffffff",
              }}
            >
              <WhatsAppIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                0985224301
              </Typography>
            </Box>

            <Box
              component="a"
              href="mailto:inscripciones@nova-xfit.com"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                textDecoration: "none",
                color: "#ffffff",
                wordBreak: "break-word",
              }}
            >
              <EmailRoundedIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                inscripciones@nova-xfit.com
              </Typography>
            </Box>
          </Stack>
        </Box>

        {!authLoading && !isAuthenticated && (
          <Stack spacing={1.1} sx={{ mb: 2 }}>
            <Button
              component={RouterLink}
              to="/login"
              onClick={handleCloseHamburgerMenu}
              startIcon={<LoginRoundedIcon />}
              sx={{
                justifyContent: "flex-start",
                width: "100%",
                px: 1.5,
                py: 1.1,
                borderRadius: 3,
                textTransform: "none",
                fontSize: 15.8,
                fontWeight: 600,
                color: "#111111",
                bgcolor: "#ffffff",
                border: "1px solid rgba(17,17,17,0.08)",
                "&:hover": {
                  color: "#e50914",
                  bgcolor: "rgba(229,9,20,0.04)",
                  borderColor: "rgba(229,9,20,0.16)",
                },
              }}
            >
              Iniciar sesión
            </Button>

            <Button
              component={RouterLink}
              to="/registro"
              onClick={handleCloseHamburgerMenu}
              sx={{
                width: "100%",
                px: 1.6,
                py: 1.1,
                borderRadius: 999,
                textTransform: "none",
                fontSize: 15.8,
                fontWeight: 600,
                color: "#ffffff",
                background:
                  "linear-gradient(135deg, #6b000a 0%, #b30f1d 42%, #e32735 72%, #111111 100%)",
                border: "1px solid rgba(0,0,0,0.10)",
                boxShadow:
                  "inset 0 1px 0 rgba(255,255,255,0.18), 0 8px 18px rgba(180,20,30,0.18)",
                "&:hover": {
                  filter: "brightness(1.05)",
                },
              }}
            >
              Registrarse
            </Button>
          </Stack>
        )}

        <Divider sx={{ mb: 2, borderColor: "rgba(17,17,17,0.08)" }} />

        <Stack spacing={1}>
          {navItems.map((item) => {
            const active = isActive(item.to);

            return (
              <Button
                key={item.label}
                component={RouterLink}
                to={item.to}
                onClick={handleCloseHamburgerMenu}
                sx={{
                  justifyContent: "flex-start",
                  width: "100%",
                  px: 1.4,
                  py: 1.15,
                  borderRadius: 3,
                  textTransform: "none",
                  fontSize: 16.2,
                  fontWeight: 600,
                  color: active ? "#e50914" : "#111111",
                  bgcolor: active ? "rgba(229,9,20,0.05)" : "#ffffff",
                  border: "1px solid rgba(17,17,17,0.06)",
                  "&:hover": {
                    color: "#e50914",
                    bgcolor: "rgba(229,9,20,0.04)",
                    borderColor: "rgba(229,9,20,0.16)",
                  },
                }}
              >
                {item.label}
              </Button>
            );
          })}

          <Button
            component={RouterLink}
            to="/contacto"
            onClick={handleCloseHamburgerMenu}
            sx={{
              justifyContent: "flex-start",
              width: "100%",
              px: 1.4,
              py: 1.15,
              borderRadius: 3,
              textTransform: "none",
              fontSize: 16.2,
              fontWeight: 600,
              color: "#111111",
              bgcolor: "#ffffff",
              border: "1px solid rgba(17,17,17,0.06)",
              "&:hover": {
                color: "#e50914",
                bgcolor: "rgba(229,9,20,0.04)",
                borderColor: "rgba(229,9,20,0.16)",
              },
            }}
          >
            Contacto
          </Button>

          {isAuthenticated && !authLoading && (
            <>
              <Button
                onClick={() => {
                  handleCloseHamburgerMenu();
                  navigate(isAdmin ? "/admin/dashboard/staff" : "/mi-perfil");
                }}
                sx={{
                  mt: 1,
                  width: "100%",
                  px: 1.5,
                  py: 1.08,
                  borderRadius: 3,
                  textTransform: "none",
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#111111",
                  border: "1px solid rgba(17,17,17,0.10)",
                  bgcolor: "#ffffff",
                }}
              >
                {isAdmin ? "Dashboard" : "Mi perfil"}
              </Button>

              <Button
                onClick={() => {
                  handleCloseHamburgerMenu();
                  handleLogout();
                }}
                sx={{
                  width: "100%",
                  px: 1.6,
                  py: 1.08,
                  borderRadius: 3,
                  textTransform: "none",
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#e50914",
                  border: "1px solid rgba(229,9,20,0.16)",
                  bgcolor: "rgba(229,9,20,0.04)",
                  "&:hover": {
                    bgcolor: "rgba(229,9,20,0.08)",
                  },
                }}
              >
                Cerrar sesión
              </Button>
            </>
          )}
        </Stack>
      </Drawer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleAccountMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 3,
            minWidth: 190,
            boxShadow: "0 18px 40px rgba(0,0,0,0.12)",
          },
        }}
      >
        <MenuItem onClick={handleAccountClick} sx={{ fontWeight: 700 }}>
          {isAdmin ? "Dashboard" : "Mi perfil"}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleAccountMenuClose();
            handleLogout();
          }}
          sx={{ fontWeight: 600, color: "#e50914" }}
        >
          Cerrar sesión
        </MenuItem>
      </Menu>
    </>
  );
}